import { useLayoutEffect, useRef, type CSSProperties, type ReactNode, type RefObject } from 'react';

import { focusRing, Icon, MonoLabel, type JudgementTone } from '@/design-system';
import type {
    PublicCosts,
    PublicLanguage,
    PublicReportChapter,
    PublicReportEntry,
} from '@/entities/publicReport';
import { cn } from '@/shared/lib';

import type { PublicCopy } from '../copy';
import { formatAmount } from '../format';
import { isModifiedClick } from '../jump';
import {
    entryMatches,
    headingMatches,
    type FindingFilter,
    type FullReportIndex,
    type IndexedEntry,
} from '../reportIndex';
import { chapterAnchor } from '../useReportView';
import { CostGrid } from './CostTable';
import { JudgementPill } from './JudgementPill';
import { ReadingGuide } from './ReadingGuide';
import { ReportPhotos } from './ReportPhotos';
import { ReportText } from './ReportText';

export type HeadingLevel = 2 | 3 | 4 | 5 | 6;

export interface ReportChapterProps {
    chapter: PublicReportChapter;
    /** De kostenblokken van de pagina, voor bovenaan het hoofdstuk Kosten. */
    costs: PublicCosts;
    /** `h2` op de publieke pagina, `h3` in de kantoorpreview. */
    level: 2 | 3;
    language: PublicLanguage;
    copy: PublicCopy;
    brandStyle: CSSProperties;
    onLinkExpired?: () => void;
    open: boolean;
    onToggle: () => void;
    index: FullReportIndex;
    filter: FindingFilter;
    /** Naar de regel van een NHG-code in de bijlage, vanaf deze bevinding. */
    onNhgJump: (code: string, from: IndexedEntry | null) => void;
}

/**
 * Eén hoofdstuk van het volledige rapport, met dezelfde inhoud als in de PDF.
 *
 * De kop is een knop die het hoofdstuk dicht- en weer openklapt (`aria-expanded`);
 * ook dichtgeklapt zegt hij het oordeel en hoeveel bevindingen erin staan. Het
 * anker (`#rapport-4`) staat op de sectie, en elke kop erin heeft er ook een
 * (`#rapport-4-1`); een kop kan de focus krijgen, zodat wie ernaartoe springt met
 * een schermlezer ook echt daar verder leest.
 *
 * Met een filter staan alleen de bevindingen die erin vallen er, onder hun
 * koppen, zodat te zien blijft waar ze zitten.
 */
export function ReportChapter({
    chapter,
    costs,
    level,
    language,
    copy,
    brandStyle,
    onLinkExpired,
    open,
    onToggle,
    index,
    filter,
    onNhgJump,
}: ReportChapterProps) {
    const anchor = chapterAnchor(chapter.key);
    const findings = index.chapterFindings.get(chapter.key) ?? 0;
    const body = useRef<HTMLDivElement | null>(null);

    useHiddenUntilFound(body, open, onToggle);

    return (
        <section
            id={anchor}
            aria-labelledby={`${anchor}-titel`}
            className="scroll-mt-[106px] border-t border-[var(--border-subtle)] first:border-t-0"
        >
            <ChapterToggle
                id={`${anchor}-titel`}
                bodyId={`${anchor}-inhoud`}
                level={level}
                number={chapter.number}
                title={chapter.title}
                open={open}
                onToggle={onToggle}
                describedBy={
                    chapter.kind === 'part'
                        ? [`${anchor}-aantal`, ...(chapter.judgement ? [`${anchor}-oordeel`] : [])]
                        : []
                }
                meta={
                    chapter.kind === 'part' ? (
                        <>
                            <span
                                id={`${anchor}-aantal`}
                                className="text-[13px] leading-[1.35] whitespace-nowrap text-[var(--text-tertiary-safe)]"
                            >
                                {findings > 0 ? copy.chapterFindings(findings) : copy.chapterQuiet}
                            </span>
                            {chapter.judgement ? (
                                <JudgementPill
                                    id={`${anchor}-oordeel`}
                                    label={chapter.judgement}
                                    tone={chapter.tone}
                                    className="shrink-0"
                                />
                            ) : null}
                        </>
                    ) : null
                }
            />

            {/*
             * Geen ruimte op dit element zelf: dichtgeklapt is het `hidden="until-found"`,
             * en dat laat in Chrome wel het vak staan (`content-visibility: hidden`),
             * met zijn padding. De ruimte zit daarom in het element eronder.
             */}
            <div ref={body} id={`${anchor}-inhoud`}>
                <div className="pt-1 pb-8">
                    {chapter.kind === 'summary' ? <ReportText text={chapter.text} /> : null}

                    {chapter.kind === 'guide' ? (
                        <ReadingGuide chapter={chapter} level={level === 2 ? 3 : 4} />
                    ) : null}

                    {chapter.kind === 'costs' ? (
                        <CostsChapter
                            chapter={chapter}
                            costs={costs}
                            language={language}
                            copy={copy}
                        />
                    ) : null}

                    {chapter.kind === 'part' ? (
                        <PartChapter
                            chapter={chapter}
                            level={level}
                            language={language}
                            copy={copy}
                            brandStyle={brandStyle}
                            index={index}
                            filter={filter}
                            onNhgJump={onNhgJump}
                            {...(onLinkExpired ? { onLinkExpired } : {})}
                        />
                    ) : null}

                    {chapter.kind === 'conditions' ? (
                        <>
                            <ReportText text={chapter.text} />
                            {chapter.footerText ? (
                                <p className="m-0 mt-[10px] text-[15px] leading-[1.6] text-[var(--text-reading)]">
                                    {chapter.footerText}
                                </p>
                            ) : null}
                            {chapter.company ? (
                                <p className="m-0 mt-3 text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)] italic">
                                    {chapter.company}
                                </p>
                            ) : null}
                        </>
                    ) : null}
                </div>
            </div>
        </section>
    );
}

/**
 * Dichtgeklapt, maar vindbaar. Met `hidden="until-found"` vindt Zoeken op de
 * pagina (Ctrl+F) ook de tekst van een dichtgeklapt hoofdstuk, en klapt de
 * browser het open; wie die waarde niet kent, leest gewoon `hidden`. React zet
 * `hidden` alleen als aan of uit, dus hier gebeurt het zelf, vóór het tekenen.
 */
function useHiddenUntilFound(
    ref: RefObject<HTMLElement | null>,
    open: boolean,
    onFound: () => void,
) {
    useLayoutEffect(() => {
        const element = ref.current;

        if (!element) {
            return;
        }

        if (open) {
            element.removeAttribute('hidden');

            return;
        }

        element.setAttribute('hidden', 'until-found');

        const found = () => onFound();

        element.addEventListener('beforematch', found);

        return () => element.removeEventListener('beforematch', found);
    }, [ref, open, onFound]);
}

/**
 * De kop van een hoofdstuk als knop, volgens het accordeonpatroon: de knop staat
 * in de kop, en de hele regel - titel, aantal, oordeel en pijl - is waar je tikt.
 * Wat naast de titel staat, leest een schermlezer als beschrijving van de knop.
 */
function ChapterToggle({
    id,
    bodyId,
    level,
    number,
    title,
    open,
    onToggle,
    meta,
    describedBy,
}: {
    id: string;
    bodyId: string;
    level: 2 | 3;
    number: string | null;
    title: string;
    open: boolean;
    onToggle: () => void;
    meta: ReactNode;
    /** De `id`'s van aantal en oordeel, los, zodat ze als twee woorden worden voorgelezen. */
    describedBy: string[];
}) {
    const Heading = `h${level}` as const;

    return (
        <div
            data-jump-row=""
            className="relative -mx-2 flex min-h-[56px] items-center gap-3 rounded-[8px] px-2 py-3 data-[jumped]:bg-[var(--accent-tint)]"
        >
            {/* Smal staan aantal en oordeel onder de titel, breed ernaast. */}
            <div className="flex min-w-0 flex-1 flex-col gap-[6px] @min-[480px]:flex-row @min-[480px]:items-center @min-[480px]:gap-3">
                <Heading
                    id={id}
                    tabIndex={-1}
                    data-chapter-heading=""
                    className="m-0 min-w-0 flex-1 font-[family-name:var(--font-display)] text-[18px] leading-[1.25] font-semibold tracking-[-0.01em] text-pretty text-[var(--text-primary)] outline-none"
                >
                    <button
                        type="button"
                        aria-expanded={open}
                        aria-controls={bodyId}
                        {...(describedBy.length > 0
                            ? { 'aria-describedby': describedBy.join(' ') }
                            : {})}
                        onClick={onToggle}
                        className={cn(
                            'w-full cursor-pointer border-0 bg-transparent p-0 text-left text-[var(--text-primary)]',
                            /* De hele regel (minstens 56 px) is de knop: een laag eroverheen, met daarop de focusrand. */
                            "outline-none after:absolute after:inset-0 after:rounded-[8px] after:content-['']",
                            'focus-visible:after:outline-2 focus-visible:after:outline-offset-2 focus-visible:after:outline-[var(--accent)]',
                        )}
                    >
                        {number ? (
                            <>
                                <span className="mr-1 font-[family-name:var(--font-mono)] text-[0.8em] font-normal tracking-normal text-[var(--text-muted-mono-safe)]">
                                    {number}
                                </span>{' '}
                            </>
                        ) : null}
                        {title}
                    </button>
                </Heading>

                {meta ? (
                    <span className="flex flex-wrap items-center gap-x-3 gap-y-1 @min-[480px]:shrink-0 @min-[480px]:justify-end">
                        {meta}
                    </span>
                ) : null}
            </div>

            <Icon
                name="chevronDown"
                size="md"
                tone="muted"
                className={cn('shrink-0', open && 'rotate-180')}
            />
        </div>
    );
}

/**
 * Een kop in het rapport: nummer, titel en het oordeel in woorden - "Matig",
 * nooit een getal - in de kleur van dat oordeel, dezelfde als in de PDF. De kop
 * is het doel van een sprong vanuit het overzicht: hij krijgt dan de focus, en
 * de regel licht even op.
 */
export function ChapterHeading({
    level,
    number,
    title,
    judgement = null,
    tone = null,
}: {
    level: HeadingLevel;
    number: string | null;
    title: string;
    judgement?: string | null;
    tone?: JudgementTone | null;
}) {
    const Heading = `h${level}` as const;

    return (
        <div
            data-jump-row=""
            className="-mx-2 mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-2 rounded-[6px] px-2 py-[2px] data-[jumped]:bg-[var(--accent-tint)]"
        >
            <Heading
                tabIndex={-1}
                data-chapter-heading=""
                className="m-0 min-w-0 font-[family-name:var(--font-display)] text-[16px] leading-[1.3] font-semibold tracking-[-0.005em] text-pretty text-[var(--text-primary)] outline-none"
            >
                {number ? (
                    <>
                        <span className="mr-1 font-[family-name:var(--font-mono)] text-[0.8em] font-normal tracking-normal text-[var(--text-muted-mono-safe)]">
                            {number}
                        </span>{' '}
                    </>
                ) : null}
                {title}
            </Heading>

            {judgement ? (
                <JudgementPill label={judgement} tone={tone} className="shrink-0" />
            ) : null}
        </div>
    );
}

function CostsChapter({
    chapter,
    costs,
    language,
    copy,
}: {
    chapter: Extract<PublicReportChapter, { kind: 'costs' }>;
    costs: PublicCosts;
    language: PublicLanguage;
    copy: PublicCopy;
}) {
    const money = (amount: number) => formatAmount(amount, language);

    return (
        <>
            <ReportText text={chapter.text} />

            {costs.blocks.length > 0 ? (
                <dl className="m-0 mt-4 grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(140px,1fr))]">
                    {costs.blocks.map((block) => (
                        <div
                            key={block.key}
                            className="rounded-[9px] border border-[var(--border-default)] px-[14px] py-3"
                        >
                            <dt className="text-[12px] leading-[1.35] text-[var(--text-tertiary-safe)]">
                                {copy.costLabels[block.key]}
                            </dt>
                            <dd className="m-0 mt-1 font-[family-name:var(--font-display)] text-[19px] leading-[1.2] font-semibold text-[var(--text-primary)]">
                                {money(block.amount)}
                            </dd>
                            {block.count !== null ? (
                                <dd className="m-0 mt-1 text-[11px] leading-[1.35] text-[var(--text-tertiary-safe)]">
                                    {copy.costParts(block.count)}
                                </dd>
                            ) : null}
                        </div>
                    ))}
                </dl>
            ) : null}

            <div className="mt-4">
                <CostGrid
                    label={chapter.title}
                    firstColumn={copy.costsColumnPart}
                    rows={chapter.parts.map((part) => ({
                        key: part.label,
                        number: null,
                        title: part.label,
                        amounts: part,
                    }))}
                    total={{ label: copy.costsTotalRow, amounts: chapter.total }}
                    language={language}
                    copy={copy}
                />
            </div>

            {chapter.groups.length > 0 ? (
                <div className="mt-6">
                    <CostGrid
                        label={copy.costsColumnGroup}
                        firstColumn={copy.costsColumnGroup}
                        rows={chapter.groups.map((group) => ({
                            key: group.name,
                            number: null,
                            title: group.name,
                            amounts: group,
                        }))}
                        total={null}
                        language={language}
                        copy={copy}
                    />
                </div>
            ) : null}
        </>
    );
}

function PartChapter({
    chapter,
    level,
    language,
    copy,
    brandStyle,
    index,
    filter,
    onNhgJump,
    onLinkExpired,
}: {
    chapter: Extract<PublicReportChapter, { kind: 'part' }>;
    level: 2 | 3;
    language: PublicLanguage;
    copy: PublicCopy;
    brandStyle: CSSProperties;
    index: FullReportIndex;
    filter: FindingFilter;
    onNhgJump: (code: string, from: IndexedEntry | null) => void;
    onLinkExpired?: () => void;
}) {
    const filtered = filter !== 'alles';
    const quiet =
        chapter.note.length === 0 &&
        chapter.entries.length === 0 &&
        chapter.headings.every((heading) => heading.entries.length === 0);

    const entries = (list: PublicReportEntry[]): ReactNode => {
        const shown = list.filter((entry) => entryMatches(entry, index, filter));

        return shown.length > 0 ? (
            <div className="flex flex-col gap-5">
                {shown.map((entry, position) => {
                    const indexed = index.of(entry);

                    return (
                        <ReportEntry
                            key={indexed?.anchor ?? `${entry.id}-${position}`}
                            entry={entry}
                            indexed={indexed ?? null}
                            language={language}
                            copy={copy}
                            brandStyle={brandStyle}
                            nhgLinked={entry.nhgCode !== null && index.nhgRows.has(entry.nhgCode)}
                            onNhgJump={onNhgJump}
                            {...(onLinkExpired ? { onLinkExpired } : {})}
                        />
                    );
                })}
            </div>
        ) : null;
    };

    return (
        <>
            {chapter.note.length > 0 && !filtered ? (
                <div className="mb-5 rounded-r-[8px] border-l-2 border-[var(--accent)] bg-[var(--surface-subtle)] px-4 py-3">
                    <ReportText text={chapter.note} />
                </div>
            ) : null}

            {quiet ? (
                <p className="m-0 text-[15px] leading-[1.6] text-[var(--text-tertiary-safe)] italic">
                    {copy.partQuiet}
                </p>
            ) : null}

            {entries(chapter.entries)}

            {chapter.headings.map((heading) =>
                filtered && !headingMatches(heading, index, filter) ? null : (
                    <div
                        key={heading.key}
                        id={chapterAnchor(heading.key)}
                        className={cn(
                            'mt-7 scroll-mt-[106px] first:mt-0',
                            heading.level === 3 && 'border-l border-[var(--border-subtle)] pl-4',
                        )}
                    >
                        <ChapterHeading
                            level={(heading.level === 3 ? level + 2 : level + 1) as HeadingLevel}
                            number={heading.number}
                            title={heading.title}
                            judgement={heading.judgement}
                            tone={heading.tone}
                        />
                        {entries(heading.entries)}
                    </div>
                ),
            )}
        </>
    );
}

/**
 * Een bevinding of opmerking: de tekst, waar het zit, wat het kost - rustig, in
 * één grijze regel zoals in de PDF - en de foto's. De NHG-code wijst naar zijn
 * regel in de bijlage.
 */
function ReportEntry({
    entry,
    indexed,
    language,
    copy,
    brandStyle,
    nhgLinked,
    onNhgJump,
    onLinkExpired,
}: {
    entry: PublicReportEntry;
    /** Waar hij staat; zijn `anchor` is waar een sprong naar deze bevinding heen gaat. */
    indexed: IndexedEntry | null;
    language: PublicLanguage;
    copy: PublicCopy;
    brandStyle: CSSProperties;
    /** Of de bijlage een regel voor zijn NHG-code heeft. */
    nhgLinked: boolean;
    onNhgJump: (code: string, from: IndexedEntry | null) => void;
    onLinkExpired?: () => void;
}) {
    const anchor = indexed?.anchor ?? null;

    return (
        <div
            id={anchor ?? undefined}
            tabIndex={anchor ? -1 : undefined}
            className={cn(
                'min-w-0 scroll-mt-[112px] rounded-[8px]',
                /* Na een sprong hierheen: een rustige rand, en even een tint; geen beweging. */
                'outline-none focus:outline-2 focus:outline-offset-[6px] focus:outline-[var(--accent)]',
                'data-[jumped]:bg-[var(--accent-tint)] data-[jumped]:outline-offset-[6px] data-[jumped]:[box-shadow:0_0_0_6px_var(--accent-tint)]',
            )}
        >
            <ReportText text={entry.text} />

            {entry.location ? (
                <p className="m-0 mt-2 text-[13px] leading-[1.5] text-pretty text-[var(--text-secondary)]">
                    <span className="font-medium text-[var(--text-primary)]">
                        {copy.findingLocation}
                    </span>{' '}
                    {entry.location}
                </p>
            ) : null}

            {entry.costs.length > 0 || entry.nhgCode ? (
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-[7px] bg-[var(--surface-subtle)] px-3 py-2">
                    {entry.costs.map((cost) => (
                        <span
                            key={cost.key}
                            className="text-[13px] leading-[1.4] text-[var(--text-secondary)]"
                        >
                            {copy.costLabels[cost.key]}{' '}
                            <span className="font-[family-name:var(--font-mono)] text-[13px] text-[var(--text-primary)]">
                                {formatAmount(cost.amount, language)}
                            </span>
                        </span>
                    ))}
                    {entry.nhgCode && nhgLinked ? (
                        <a
                            href="#nhg"
                            aria-label={copy.nhgJump(entry.nhgCode)}
                            onClick={(event) => {
                                if (isModifiedClick(event)) {
                                    return;
                                }

                                event.preventDefault();

                                if (entry.nhgCode) {
                                    onNhgJump(entry.nhgCode, indexed);
                                }
                            }}
                            className={cn(
                                '-my-2 ml-auto inline-flex min-h-[44px] items-center rounded-[6px] px-2',
                                'font-[family-name:var(--font-mono)] text-[11px] tracking-[0.02em] text-[var(--accent-dark)] underline decoration-[var(--accent-border)] underline-offset-2',
                                'hover:text-[var(--accent)] hover:decoration-[var(--accent)]',
                                focusRing,
                            )}
                        >
                            {copy.nhgRow(entry.nhgCode)}
                        </a>
                    ) : entry.nhgCode ? (
                        <MonoLabel size={11} variant="value" tone="muted" className="ml-auto">
                            {copy.nhgRow(entry.nhgCode)}
                        </MonoLabel>
                    ) : null}
                </div>
            ) : null}

            <ReportPhotos
                photos={entry.photos}
                copy={copy}
                brandStyle={brandStyle}
                {...(onLinkExpired ? { onLinkExpired } : {})}
            />
        </div>
    );
}
